// apiAuth — every same-origin /api request carries the signed-in session.
//
// The server refuses an /api route with no credential (api/_lib/route-auth.js,
// audit 2026-10-06). Most callers are a plain fetch('/api/...'), so rather than
// edit each one, main.jsx's fetch wrapper asks withApiAuth for the request to
// send. Rules:
//   • only our own origin's /api/ paths — the token never leaves for a third
//     party, even one whose path happens to start with /api/;
//   • a caller that already set Authorization (a janetFetch, a cron-secret
//     call) is left exactly as it was;
//   • no session → the request goes out unchanged and the server answers 401.

export function isSameOriginApi(input, origin) {
  try {
    const s = typeof input === 'string' ? input : (input?.url || String(input || ''))
    if (!s) return false
    const u = new URL(s, origin)
    return u.origin === origin && u.pathname.startsWith('/api/')
  } catch { return false }
}

function hasAuthorization(headers) {
  if (!headers) return false
  if (typeof headers.has === 'function') return headers.has('authorization')
  if (Array.isArray(headers)) return headers.some(([k]) => String(k).toLowerCase() === 'authorization')
  return Object.keys(headers).some(k => k.toLowerCase() === 'authorization')
}

// → the init to send. Untouched unless a token is attached.
export async function withApiAuth(input, init = {}, { origin, getToken }) {
  if (!isSameOriginApi(input, origin)) return init
  const requestHeaders = typeof input === 'object' && input?.headers
  if (hasAuthorization(init.headers) || hasAuthorization(requestHeaders)) return init
  let token = null
  try { token = await getToken() } catch { /* no session */ }
  if (!token) return init
  // init.headers replaces a Request's own headers wholesale, so start from them.
  const headers = new Headers(requestHeaders || undefined)
  new Headers(init.headers || undefined).forEach((v, k) => headers.set(k, v))
  headers.set('Authorization', `Bearer ${token}`)
  return { ...init, headers }
}
